// REMIX HMR BEGIN
import * as __hmr__ from "remix:hmr";
if (import.meta) {
import.meta.hot = __hmr__.createHotContext(
//@ts-expect-error
"app/components/helpers/htmlParser.tsx"
);
import.meta.hot.lastModified = "1732036225882.6924";
}
// REMIX HMR END

import parse, { HTMLReactParserOptions, Element, domToReact, Text } from 'html-react-parser';
import { ParagraphCodeBlock } from "~/components/paragraph";
import { Heading as HeadingElement, Link} from "~/components";
import { generate } from "randomstring";
import { Heading } from '~/@types/helpers';


const getKey = () => {
  return generate({
    length: 4,
    charset: "alphabetic",
  });
};

const classNameOverrides: { [key: string]: string } = {
  "text-align-center": "text-center",
  "text-align-right": "text-right",
  "text-align-justify": "text-justify",
};

function codeBlockParser({
  html,
  language,
}: {
  html: string;
  language: string;
}) {
  return (
    <div key={getKey()} className="max-w-[calc(100vw-56px)] md:max-w-none ">
      <ParagraphCodeBlock code={html} language={language} />
    </div>
  );
}

type LinkTheme = "dark" | "light" | "lighter";

function linkParser({
  href,
  children,
  target,
  theme,
}: {
  href: string;
  children: string | JSX.Element;
  target?: string;
  theme: LinkTheme;
}) {
  return (
    <Link
      key={getKey()}
      to={href}
      target={target ? target : undefined}
      rel="noopener noreferrer"
      theme={theme}
    >
      {children}
    </Link>
  );
}

type DataElement = {
  data: string;
  type: "text";
};

type TagElement = {
  type: "tag";
  name: string;
  children: TagElement[] | DataElement[];
};

function recursiveTextParser({
  children,
}: {
  children: TagElement[] | DataElement[];
}) {
  const resolved = children.map((child) => {
    if (child.type === "text") {
      return child.data;
    }
    const { name, children } = child;
    if (name === "strong") {
      return (
        <strong key={getKey()}>{recursiveTextParser({ children })}</strong>
      );
    }
    if (name === "em") {
      return <em key={getKey()}>{recursiveTextParser({ children })}</em>;
    }
    if (name === "s") {
      return <s key={getKey()}>{recursiveTextParser({ children })}</s>;
    }    
    if (name === "span") {
      return <span>{recursiveTextParser({ children })}</span>;
    }
  });

  return resolved.at(0) || "";
}

function headingParser({ tag, text, className }:{ tag: string; text: string; className: string; }) {
    return <HeadingElement children={text} tag={tag as Heading} className={className} />
}

export default function htmlParser({ html, theme = "light" }: { html: string, theme?: LinkTheme }) {

  const options: HTMLReactParserOptions = {
    replace(domNode) {
      if (domNode instanceof Element && domNode.attribs) {
        if (domNode.name === "pre" && domNode.children[0].name === "code") {
          const codeTag = domNode.children[0];
          return codeBlockParser({
            html: codeTag.children[0].data,
            language: codeTag.attribs.class.replace("language-", ""),
          });
        }
        if (domNode.type === "tag" && domNode.name === "a") {
          const hasElementAsChild = domNode.children[0].type === "tag";
          if (hasElementAsChild) {
            return linkParser({
              href: domNode.attribs.href,
              target: domNode.attribs.target,
              children: recursiveTextParser({ children: domNode.children }),
              theme
            });
          }
  
          return linkParser({
            href: domNode.attribs.href,
            target: domNode.attribs.target,
            children: domNode.children[0].data,
            theme
          });
        }
        if (domNode.type === "tag" && domNode.name === "p") {
          const className = classNameOverrides[domNode.attribs.class];
          if (domNode.attribs.class && className) {
            domNode.attribs.class = className;
          }
          return <p className={domNode.attribs.class}>{domToReact(domNode.children, options)}</p>
        }
        if (domNode.type === "tag" && /^h[1-3]$/.test(domNode.name)) {
          return headingParser({
            tag: domNode.name,
            text: (domNode.children[0] as Text).data,
            className: domNode.attribs.class,
          });
        }
      }
    },
  };
  return parse(html, options);

}
